import React, { useState } from 'react';
import { Mail, User, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { addAllowedEmail } from '@/services/allowedEmails';

interface AddEmailFormProps {
  onSuccess: () => Promise<void>;
}

export const AddEmailForm: React.FC<AddEmailFormProps> = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setIsLoading(true);

    try {
      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setStatus({ type: 'error', message: 'Please enter a valid email address.' });
        setIsLoading(false);
        return;
      }

      // Validate display name
      if (!displayName.trim()) {
        setStatus({ type: 'error', message: 'Display name is required.' });
        setIsLoading(false);
        return;
      }

      // Adding a user never grants admin. Promote deliberately, in the admin panel.
      await addAllowedEmail(email.toLowerCase(), displayName.trim(), true, false);

      setStatus({ type: 'success', message: 'User successfully added to the system.' });
      setEmail('');
      setDisplayName('');
      await onSuccess();
    } catch (error) {
      console.error('Error adding user:', error);
      setStatus({ type: 'error', message: 'Failed to add user. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-start pt-8">
      <Card className="w-full max-w-md border shadow-lg bg-card/50 backdrop-blur-sm">

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="pl-9"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="displayName" className="text-sm font-medium">
                  Display Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="John Doe"
                    className="pl-9"
                    required
                  />
                </div>
              </div>
            </div>

            {status && (
              <Alert
                variant={status.type === 'success' ? 'default' : 'destructive'}
                className={status.type === 'success' ? 'border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400' : ''}
              >
                {status.type === 'success' ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                <AlertTitle>{status.type === 'success' ? 'Success' : 'Error'}</AlertTitle>
                <AlertDescription>{status.message}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary/90 h-11 text-base font-medium transition-all duration-200"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Adding User...
                </div>
              ) : (
                'Add User'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
